import React, { useEffect, useId, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import Child from '@rover/icons/svg/child.svg';
import Pets from '@rover/icons/svg/pets.svg';
import { Button, Flex, Paragraph } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';
import {
  type PetCompatibilityAnswerEnum as Answer,
  PetCompatibilityAnswerEnum,
} from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityAnswerEnum';
import {
  type PetCompatibilityQuestionEnum as Question,
  PetCompatibilityQuestionEnum,
} from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityQuestionEnum';

type Props = {
  question: Question;
  sitterName: string;
  petName: string | null;
  position: number;
  total: number;
  pendingAnswer: Answer | undefined;
  hasSaveError: boolean;
  onAnswer: (question: Question, answer: Answer) => void;
};

const PetCompatibilityQuestion = ({
  question,
  sitterName,
  petName,
  position,
  total,
  pendingAnswer,
  hasSaveError,
  onAnswer,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const titleId = useId();
  const progressId = useId();
  const errorId = useId();
  const groupRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (position > 1) groupRef.current?.querySelector('button')?.focus();
  }, [position]);

  const titles: Record<Question, string> = petName
    ? {
        dogs: t`${sitterName} has dogs at home. Does ${petName} get along with dogs?`,
        cats: t`${sitterName} has cats at home. Does ${petName} get along with cats?`,
        kids: t`${sitterName} has children at home. Does ${petName} get along with kids?`,
      }
    : {
        dogs: t`${sitterName} has dogs at home. Do your pets get along with dogs?`,
        cats: t`${sitterName} has cats at home. Do your pets get along with cats?`,
        kids: t`${sitterName} has children at home. Do your pets get along with kids?`,
      };
  const options = [
    { value: PetCompatibilityAnswerEnum.yes, title: t`Yes` },
    { value: PetCompatibilityAnswerEnum.no, title: t`No` },
    { value: PetCompatibilityAnswerEnum.unsure, title: t`Unsure` },
  ];

  return (
    <Banner
      severity="info"
      icon={question === PetCompatibilityQuestionEnum.kids ? Child : Pets}
      suppressDefaultTypography
      containerProps={{ webProps: { 'aria-live': 'off' } }}
      testID="pet-compatibility-question"
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap="2x" pt="1x">
        <Paragraph size="100" fontWeight="semibold" mb="0x" webProps={{ id: titleId }}>
          {titles[question]}
        </Paragraph>
        {total > 1 && (
          <>
            <Paragraph
              size="100"
              textColor="secondary"
              mb="0x"
              sx={{ whiteSpace: 'nowrap' }}
              webProps={{ 'aria-hidden': true }}
            >
              {`(${position}/${total})`}
            </Paragraph>
            <Paragraph webProps={{ id: progressId, hidden: true }}>
              {t`Question ${position} of ${total}`}
            </Paragraph>
          </>
        )}
      </Flex>
      <Flex
        ref={groupRef}
        flexWrap="wrap"
        gap="2x"
        pt="2x"
        webProps={{
          role: 'group',
          'aria-labelledby': total > 1 ? `${titleId} ${progressId}` : titleId,
          'aria-describedby': hasSaveError ? errorId : undefined,
          'aria-busy': pendingAnswer !== undefined,
        }}
      >
        {options.map(({ value, title }) => (
          <Button
            key={value}
            size="small"
            loading={pendingAnswer === value}
            onPress={() => onAnswer(question, value)}
          >
            {title}
          </Button>
        ))}
      </Flex>
      <Paragraph
        size="100"
        textColor="error"
        mt={hasSaveError ? '2x' : '0x'}
        mb="0x"
        webProps={{ id: errorId, role: 'alert' }}
      >
        {hasSaveError && t`Something went wrong. Please try again.`}
      </Paragraph>
    </Banner>
  );
};

export default PetCompatibilityQuestion;
